'use client'

import {
  Badge,
  Checkbox,
  cn,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui'
import {
  Bot,
  Calendar,
  CalendarClock,
  CalendarPlus,
  Clock,
  Clock3,
  User,
  UserCheck,
  UsersRound,
} from 'lucide-react'

import type { SchedulingAssignmentStrategy } from '@/types/agents'
import type { SchedulingProfile } from '@/types/appointments'

interface SchedulingTabProps {
  linkedProfileIds: string[]
  schedulingProfiles: SchedulingProfile[]
  agentId: string
  strategy: SchedulingAssignmentStrategy
  onStrategyChange: (strategy: SchedulingAssignmentStrategy) => void
  onProfileToggle: (profileId: string, checked: boolean) => void
  isUpdatingProfiles?: boolean
}

const STRATEGIES = [
  {
    value: 'ROUND_ROBIN',
    label: 'Revezamento',
    description:
      'Alterna entre os vendedores livres para equilibrar as reuniões.',
    useWhen:
      'Recomendado quando todos atendem o mesmo público e devem receber oportunidades igualmente.',
    icon: UsersRound,
  },
  {
    value: 'LEAD_OWNER',
    label: 'Responsável pelo lead',
    description: 'Prioriza a agenda do responsável atual pelo lead.',
    useWhen:
      'Se ele não estiver livre, o sistema reveza entre os demais vendedores disponíveis.',
    icon: UserCheck,
  },
  {
    value: 'BY_AVAILABILITY',
    label: 'Por disponibilidade',
    description: 'Escolhe o primeiro perfil livre na ordem configurada.',
    useWhen:
      'Use quando encontrar um horário rapidamente importa mais que equilibrar a distribuição.',
    icon: Clock3,
  },
  {
    value: 'LLM',
    label: 'A IA decide',
    description: 'A IA escolhe o vendedor pelo contexto da conversa.',
    useWhen:
      'Use somente quando o prompt possui regras claras por região, produto ou especialidade.',
    icon: Bot,
  },
] satisfies Array<{
  value: SchedulingAssignmentStrategy
  label: string
  description: string
  useWhen: string
  icon: typeof CalendarClock
}>

const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

function formatAvailability(profile: SchedulingProfile) {
  const windows =
    (profile.availabilityWindows ?? []).length > 0
      ? profile.availabilityWindows
      : profile.availableDays.map((dayOfWeek) => ({
          dayOfWeek,
          start: profile.availableFrom,
          end: profile.availableTo,
        }))

  if (windows.length === 0) return 'Sem disponibilidade'

  const grouped = new Map<number, string[]>()
  for (const window of windows) {
    const current = grouped.get(window.dayOfWeek) ?? []
    current.push(`${window.start}-${window.end}`)
    grouped.set(window.dayOfWeek, current)
  }

  return [...grouped.entries()]
    .sort(([a], [b]) => a - b)
    .map(
      ([dayOfWeek, ranges]) => `${DAY_LABELS[dayOfWeek]} ${ranges.join(', ')}`
    )
    .join(' · ')
}

export function SchedulingTab({
  linkedProfileIds,
  schedulingProfiles,
  agentId,
  strategy,
  onStrategyChange,
  onProfileToggle,
  isUpdatingProfiles = false,
}: SchedulingTabProps) {
  const selectedProfiles = schedulingProfiles.filter((profile) =>
    linkedProfileIds.includes(profile.id)
  )

  return (
    <div className="flex flex-col gap-8">
      <section>
        <h3 className="text-sm font-semibold">Agendas disponíveis</h3>
        <p className="text-muted-foreground mt-1 text-xs">
          Vincule um perfil para cada vendedor que poderá receber reuniões
          marcadas por este agente.
        </p>

        <div className="mt-4 flex items-center justify-between gap-3">
          <Label>Perfis vinculados</Label>
          <Badge variant="secondary">
            {selectedProfiles.length} selecionado
            {selectedProfiles.length === 1 ? '' : 's'}
          </Badge>
        </div>

        {schedulingProfiles.length === 0 ? (
          <div className="border-border bg-muted/30 mt-3 flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center">
            <CalendarPlus className="text-muted-foreground size-7" />
            <p className="text-sm font-medium">
              Nenhum perfil de agendamento criado
            </p>
            <p className="text-muted-foreground max-w-md text-xs">
              Crie um perfil para cada vendedor em Agendamentos antes de
              vinculá-lo ao agente.
            </p>
          </div>
        ) : (
          <div className="mt-3 grid gap-3">
            {schedulingProfiles.map((profile) => {
              const selected = linkedProfileIds.includes(profile.id)
              const usedByAnotherAgent =
                Boolean(profile.agentId) && profile.agentId !== agentId

              return (
                <label
                  key={profile.id}
                  className={cn(
                    'border-border bg-card flex gap-3 rounded-lg border p-4 transition-colors',
                    usedByAnotherAgent
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:border-primary/40 cursor-pointer',
                    selected && 'border-primary/50 bg-primary/5'
                  )}
                >
                  <Checkbox
                    checked={selected}
                    disabled={usedByAnotherAgent || isUpdatingProfiles}
                    onCheckedChange={(checked) =>
                      onProfileToggle(profile.id, checked === true)
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold">{profile.name}</p>
                      {usedByAnotherAgent && (
                        <Badge variant="outline">
                          Vinculado a outro agente
                        </Badge>
                      )}
                      {!profile.isActive && (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                    </div>
                    <div className="text-muted-foreground mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                      <span className="flex items-center gap-1.5">
                        <User className="size-3.5" />
                        {profile.ownerUser
                          ? (profile.ownerUser.name ?? profile.ownerUser.email)
                          : 'Sem vendedor responsável'}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="size-3.5" />
                        {profile.durationMinutes} min
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Calendar className="size-3.5" />
                        {formatAvailability(profile)}
                      </span>
                    </div>
                  </div>
                </label>
              )
            })}
          </div>
        )}
      </section>

      <section>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <h3 className="text-sm font-semibold">Distribuição das reuniões</h3>
            <p className="text-muted-foreground mt-1 text-xs">
              Define quem recebe o agendamento quando mais de um perfil está
              vinculado.
            </p>
          </div>
          <Select
            value={strategy}
            onValueChange={(value) =>
              onStrategyChange(value as SchedulingAssignmentStrategy)
            }
          >
            <SelectTrigger className="w-full sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STRATEGIES.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {STRATEGIES.map((item) => {
            const Icon = item.icon
            const selected = strategy === item.value
            return (
              <button
                key={item.value}
                type="button"
                className={cn(
                  'border-border bg-card hover:border-primary/40 hover:bg-muted/30 rounded-lg border p-4 text-left transition-colors',
                  selected && 'border-primary/50 bg-primary/5'
                )}
                onClick={() => onStrategyChange(item.value)}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      'bg-muted flex size-8 shrink-0 items-center justify-center rounded-md',
                      selected && 'bg-primary/10 text-primary'
                    )}
                  >
                    <Icon className="size-4" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{item.label}</span>
                      {selected && (
                        <Badge variant="secondary">Selecionado</Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {item.description}
                    </p>
                    <p className="text-muted-foreground mt-2 text-xs">
                      {item.useWhen}
                    </p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        <div className="border-primary/20 bg-primary/5 text-muted-foreground mt-4 rounded-lg border px-4 py-3 text-xs">
          Somente perfis ativos e livres participam da escolha. O vendedor
          selecionado passa a ser o responsável pelo lead e atendimento.
        </div>
      </section>
    </div>
  )
}
